import React, { useEffect, useState } from "react";
import { Col, Input, Modal, Row, Select, Switch, Table } from "antd";
import { cloneDeep, get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { paginationDefaults } from "../../../utils/properties";
import { setSection } from "../../../redux/ducks/Section";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { checkRole, formatNumber, unformatNumber, trimData, showSuccess, showError } from "../../../utils/utilities";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  WarningOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  StopOutlined,
} from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { useFormik } from "formik";
import OrderAllocation from "../../../services/api/OrderAllocation";
import { setLoading } from "../../../redux/ducks/Loading";
const { Option } = Select;

const OdaAllocationDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { confirm } = Modal;
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const location = useLocation();
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    isAdmin: userInfo.roles ? checkRole(userInfo.roles, "SYSTEM_IT") : false,
    tableContent: [],
    expandedTableContent: [],
    expandedOrder: {},
    expandedRowKeys: [],
    expandedRow: null,
    disableNewButton: false,
    newProjectTaskModal: {
      project: null,
      projectTask: null,
      visible: false,
    },
    projectsListOptions: [],
    projectsTaskListOptions: [],
    pagination: {
      currentPage: paginationDefaults.page,
      pageSize: paginationDefaults.size,
      totalItems: paginationDefaults.total,
    },
    editRateModal: {
      visible: false,
      id: "",
      rate: "",
    },
    loading: false,
  });

  const openRateModal = (id, rate) => {
    setState((prevState) => ({
      ...prevState,
      editRateModal: { visible: true, id, rate },
    }));
  };

  const closeRateModal = () => {
    formikEditRate.resetForm();
    setState((prevState) => ({
      ...prevState,
      editRateModal: { visible: false, id: "", rate: "" },
    }));
  };

  const formikEditRate = useFormik({
    initialValues: {
      rate: get(state.editRateModal, "rate", 0),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.rate) {
        errors.rate = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const datasave = {
        orderId: state.editRateModal.id,
        hourlyRate: unformatNumber(values.rate, currentLanguage),
      };
      setState((prevState) => ({ ...prevState, loading: true }));
      const response = await OrderAllocation.updateHourlyRate(datasave);
      if (!response.error) {
        setState((prevState) => ({
          ...prevState,
          disableNewButton: datasave.hourlyRate === 0,
        }));
        showSuccess();
        getOdaList();
        closeRateModal();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const formikNewProjectTask = useFormik({
    initialValues: {
      project: null,
      projectTask: null,
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.project) {
        errors.project = "Required!";
      }
      if (!values.projectTask) {
        errors.projectTask = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      submitNewOdaProjectTask(values);
    },
  });

  const columns = [
    {
      title: t("odaAllocation.table.oda"),
      dataIndex: "id",
      key: "id",
      render: (value, row) => {
        return (
          <div>
            <span>{row.statusOrder === "BLOCKED" ? <StopOutlined className="PFred mr-10" /> : ""}</span>
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      title: t("salApproval.table.statusOrder"),
      dataIndex: "statusOrder",
      key: "statusOrder",
      responsive: ["lg"],
    },
    {
      title: t("salApproval.table.costCentreId"),
      dataIndex: "costCentreId",
      key: "costCentreId",
      responsive: ["lg"],
    },
    {
      title: t("odaAllocation.table.productCategoryId"),
      dataIndex: "productCategoryId",
      key: "productCategoryId",
    },
    {
      title: t("odaAllocation.table.hourlyRate"),
      dataIndex: "hourlyRate",
      key: "hourlyRate",
      className: "table-value-right",
      render: (value, row) => {
        let hourlyRate = "-";
        if (value === 0) hourlyRate = "-";
        else if (row.productCategoryType === "HOUR")
          hourlyRate = <div className="oneline"> {formatNumber({ value, lang: currentLanguage, suffix: "EUR" })}</div>;
        return hourlyRate;
      },
      responsive: ["lg"],
    },
    {
      title: t("odaAllocation.table.totalOrder"),
      dataIndex: "totalOrder",
      key: "totalOrder",
      className: "table-value-right",
      render: (value, row) => {
        let totalOrder = "";
        if (row.productCategoryType === "HOUR" && row.hourlyRate === 0) totalOrder = "100%";
        else
          totalOrder = (
            <div className="oneline">
              {" "}
              {formatNumber({ value, lang: currentLanguage, suffix: row.productCategoryType })}
            </div>
          );
        return totalOrder;
      },
      responsive: ["lg"],
    },
    {
      title: t("odaAllocation.table.receivedAmount"),
      dataIndex: "receivedAmount",
      key: "receivedAmount",
      className: "table-value-right",
      render: (value, row) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: row.productCategoryType,
        }),
      responsive: ["lg"],
    },
    {
      title: t("odaAllocation.table.residualAmount"),
      dataIndex: "residualAmount",
      key: "residualAmount",
      className: "table-value-right",
      render: (value, row) => (
        <div className="oneline">{formatNumber({ value, lang: currentLanguage, suffix: row.productCategoryType })}</div>
      ),
      responsive: ["lg"],
    },
    {
      title: t("odaAllocation.table.netValue"),
      dataIndex: "netValue",
      key: "netValue",
      className: "table-value-right",
      render: (value) => <div className="oneline">{formatNumber({ value, lang: currentLanguage, suffix: "EUR" })}</div>,
      responsive: ["md"],
    },
    {
      title: t("odaAllocation.table.receivedValue"),
      dataIndex: "receivedValue",
      key: "receivedValue",
      className: "table-value-right",
      render: (value) => <div className="oneline">{formatNumber({ value, lang: currentLanguage, suffix: "EUR" })}</div>,
    },
    {
      title: t("odaAllocation.table.residualValue"),
      dataIndex: "residualValue",
      key: "residualValue",
      className: "table-value-right",
      render: (value) => <div className="oneline">{formatNumber({ value, lang: currentLanguage, suffix: "EUR" })}</div>,
      responsive: ["md"],
    },
    {
      title: "",
      dataIndex: "alert",
      key: "alert",
      render: (value, row) => {
        return (
          <div className="space-around">
            {(state.isAdmin && row.productCategoryType === "HOUR" && value) ||
            (!state.isAdmin && row.productCategoryType === "HOUR" && value && row.statusOrder !== "BLOCKED") ? (
              <div onClick={() => openRateModal(row.id, row.hourlyRate)}>
                <EditOutlined className="cursor-pointer mr-10" />
              </div>
            ) : (
              ""
            )}
            {value ? (
              <div>
                <WarningOutlined className="PFred mr-10" />
              </div>
            ) : (
              ""
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!isEmpty(params.supplierId)) {
      dispatch(
        setSection({
          section: {
            title: `${params.supplierId} | ${location.state.supplierName}`,
          },
        })
      );
    } else {
      navigate("/oda-allocation");
    }
    getOdaList();
  }, []);

  const getOdaList = async (page = state.pagination.currentPage) => {
    dispatch(setLoading(true));
    let odaList = await OrderAllocation.listOdaBySupplierId(
      `page=${page - 1}&size=${state.pagination.pageSize}&supplierId=${params.supplierId}${filters.obj.status ? `&status=${filters.obj.status}` : ""}`
    );
    if (!odaList.error || !isEmpty(odaList.data)) {
      odaList.data.forEach((data, idx) => {
        data.key = idx;
      });
    } else {
      showError(get(odaList, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, tableContent: odaList.data }));
    setState((prevState) => ({
      ...prevState,
      pagination: {
        currentPage: odaList.currentPage ? odaList.currentPage + 1 : paginationDefaults.page,
        pageSize: get(odaList, "pageSize", paginationDefaults.size),
        totalItems: get(odaList, "totalItems", paginationDefaults.totalItems),
      },
    }));
    dispatch(setLoading(false));
  };

  const expandedRowRender = () => {
    const columns = [
      {
        title: t("general.enabled"),
        dataIndex: "enabled",
        key: "enabled",
        render: (value, row) => {
          return (
            <Switch
              disabled={!state.isAdmin && state.expandedOrder.statusOrder === "BLOCKED"}
              onChange={() => {
                enableProjectTask(row);
              }}
              checked={value}
            />
          );
        },
      },
      {
        title: t("odaAllocation.table.pmName"),
        dataIndex: "namePmProject",
        key: "namePmProject",
      },
      {
        title: t("odaAllocation.table.project"),
        dataIndex: "projectId",
        key: "projectId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectName}
            </div>
          );
        },
        responsive: ["md"],
      },
      {
        title: t("odaAllocation.table.projectTask"),
        dataIndex: "projectTaskId",
        key: "projectTaskId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectTaskName}
            </div>
          );
        },
      },
      {
        title: t("odaAllocation.table.receivedAmount"),
        dataIndex: "receivedAmount",
        key: "receivedAmount",
        className: "table-value-right",
        render: (value, row) =>
          formatNumber({
            value,
            lang: currentLanguage,
            suffix: row.productCategoryType,
          }),
        responsive: ["md"],
      },
      {
        title: t("odaAllocation.table.receivedValue"),
        dataIndex: "receivedValue",
        key: "receivedValue",
        className: "table-value-right",
        render: (value) => formatNumber({ value, lang: currentLanguage, suffix: "EUR" }),
        responsive: ["md"],
      },
      {
        title: t("odaAllocation.table.statusProject"),
        dataIndex: "projectStatus",
        key: "projectStatus",
        responsive: ["md"],
      },
      {
        title: t("odaAllocation.table.statusTask"),
        dataIndex: "projectTaskStatus",
        key: "projectTaskStatus",
        responsive: ["md"],
      },
      {
        align: "center",
        title: t("odaAllocation.table.timeRegistration"),
        dataIndex: "timeRegistration",
        key: "timeRegistration",
        render: (value) =>
          value ? <CheckOutlined className="icon-medium" /> : <CloseOutlined className="icon-medium" />,
        responsive: ["md"],
      },
      {
        title: t(""),
        dataIndex: "",
        key: "x",
        width: 50,
        render: (record) => {
          return (
            <div className="space-around">
              {(record.receivedValue === 0 || record.receivedValue === null) &&
                (record.receivedAmount === 0 || record.receivedAmount === null) && (
                  <div onClick={() => deleteProjectTaskCallback(record)}>
                    <DeleteOutlined
                      className="icon-medium"
                      disabled={!state.isAdmin && state.expandedOrder.statusOrder === "BLOCKED"}
                    />
                  </div>
                )}
            </div>
          );
        },
      },
    ];
    return (
      <>
        <Table columns={columns} dataSource={state.expandedTableContent} pagination={false} />
        <Row align="middle" justify="end" className="mt-10">
          <Col>
            <Button
              isDisabled={state.disableNewButton}
              currentIcon={<PlusOutlined className="icon-medium" />}
              clickCallback={() => newProjectTask()}
            />
          </Col>
        </Row>
      </>
    );
  };

  const getExpandedRowData = async (expanded, record = state.expandedOrder) => {
    let rowData = [];
    let disableButton = false;
    const keys = [];
    if (expanded) {
      dispatch(setLoading(true));
      const projectsTasks = await OrderAllocation.listOdaProjectsByOderId(`orderId=${record.id}`);
      if (!projectsTasks.error) {
        keys.push(record.key);
        rowData = cloneDeep(projectsTasks);
        if (
          (record.hourlyRate === 0 && record.productCategoryType === "HOUR") ||
          (!state.isAdmin && (record.statusOrder === "BLOCKED" || record.status === "COMPLETED"))
        ) {
          disableButton = true;
        }
      } else {
        showError(get(projectsTasks, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    }
    if (!isEmpty(rowData.data)) {
      rowData.data.forEach((projectTask, idx) => {
        projectTask.key = idx;
        projectTask.productCategoryType = record.productCategoryType;
      });
    }
    setState((prevState) => ({
      ...prevState,
      expandedRowKeys: keys,
      expandedTableContent: rowData.data,
      expandedOrder: record,
      disableNewButton: disableButton,
    }));
  };

  const enableProjectTask = async (rowData) => {
    const selectedProjectTask = cloneDeep(state.expandedTableContent);
    selectedProjectTask[rowData.key].enabled = !selectedProjectTask[rowData.key].enabled;
    setState((prevState) => ({
      ...prevState,
      expandedTableContent: selectedProjectTask,
    }));

    const enableRequest = {
      enabled: selectedProjectTask[rowData.key].enabled,
      orderId: rowData.orderId,
      projectTaskId: rowData.projectTaskId,
    };

    const enableResponse = await OrderAllocation.updateOdaProjectTaskEnabled(enableRequest);
    if (!enableResponse.error) {
      // CAPIRE SE E" EFFETTIVAMENTE NECESSARIO FARE LA CHIAMATA DI REFRESH DEI DATI
      getExpandedRowData(true);
      showSuccess();
    } else showError(get(enableResponse, "error.messageCode", "ER000"));
  };

  const newProjectTask = () => {
    setState((prevState) => ({ ...prevState, newProjectTaskModal: { visible: true } }));
  };

  const onSearchProject = async (value) => {
    if (value.length >= 3) {
      setState((prevState) => ({ ...prevState, newProjectTaskModal: { ...state.newProjectTaskModal } }));
      const projects = await OrderAllocation.listOdaProjects(`filter=${value}`);
      if (!projects.error && projects.data) {
        setState((prevState) => ({
          ...prevState,
          projectsListOptions: projects.data,
          newProjectTaskModal: { ...state.newProjectTaskModal },
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, projectsListOptions: [] }));
    }
  };

  const onSearchProjectTask = async (value) => {
    if (value.length >= 3) {
      setState((prevState) => ({ ...prevState, newProjectTaskModal: { ...state.newProjectTaskModal } }));
      const projectTasks = await OrderAllocation.listOdaProjectsTask(
        `orderId=${state.expandedOrder.id}&projectId=${formikNewProjectTask.values.project}&filter=${value}`
      );
      if (!projectTasks.error && projectTasks.data) {
        setState((prevState) => ({
          ...prevState,
          projectsTaskListOptions: projectTasks.data,
          newProjectTaskModal: { ...state.newProjectTaskModal },
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, projectsTaskListOptions: [] }));
    }
  };

  const initialProjectTaskList = async (value) => {
    setState((prevState) => ({
      ...prevState,
      newProjectTaskModal: { ...state.newProjectTaskModal },
    }));
    const projectTasks = await OrderAllocation.listOdaProjectsTask(
      `orderId=${state.expandedOrder.id}&projectId=${value}&filter=`
    );
    if (!projectTasks.error && projectTasks.data) {
      setState((prevState) => ({
        ...prevState,
        projectsTaskListOptions: projectTasks.data,
        newProjectTaskModal: { ...state.newProjectTaskModal },
      }));
    }
  };

  const closeModal = () => {
    setState((prevState) => ({
      ...prevState,
      newProjectTaskModal: { visible: false },
      projectsListOptions: [],
      projectsTaskListOptions: [],
    }));
    formikNewProjectTask.setFieldValue("project", null);
    formikNewProjectTask.setFieldValue("projectTask", null);
  };

  const submitNewOdaProjectTask = async (values) => {
    setState((prevState) => ({
      ...prevState,
      newProjectTaskModal: { ...state.newProjectTaskModal },
      loading: true,
    }));
    const saveRequest = {
      enabled: true,
      orderId: state.expandedOrder.id,
      projectTaskId: values.projectTask,
    };
    const saveResponse = await OrderAllocation.saveNewOda(saveRequest);
    if (!saveResponse.error) {
      showSuccess();
      setState((prevState) => ({
        ...prevState,
        newProjectTaskModal: { ...state.newProjectTaskModal },
      }));
      closeModal();
      getOdaList();
      getExpandedRowData(true);
    } else showError(get(saveResponse, "error.messageCode", "ER000"));
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteProjectTaskCallback = (selectedProjectTask) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await OrderAllocation.deleteOdaProjectTask(selectedProjectTask.id);
        if (!deleteResponse.error) {
          showSuccess();
          getOdaList();
          getExpandedRowData(true);
        } else showError(get(deleteResponse, "error.messageCode", "ER000"));
      },
      onCancel() {},
    });
  };

  return (
    <>
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.pageSize,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getOdaList(page);
          },
        }}
        columns={columns}
        expandable={{ expandedRowRender }}
        expandedRowKeys={state.expandedRowKeys}
        onExpand={(expanded, record) => getExpandedRowData(expanded, record)}
        dataSource={state.tableContent}
        size="small"
        scroll={{ x: true }}
      />
      <Modal
        open={state.newProjectTaskModal.visible}
        width={700}
        title={t("odaAllocation.newProjectTask")}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikNewProjectTask.submitForm}
            isDisabled={!(formikNewProjectTask.dirty && formikNewProjectTask.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="project">
                {t("odaAllocation.table.project")}*
              </label>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  setState((prevState) => ({ ...prevState, projectsListOptions: [] }));
                }}
                className="w-100"
                id="project"
                optionFilterProp="value"
                name="project"
                showSearch
                placeholder={t("odaAllocation.table.project")}
                onChange={(value) => {
                  formikNewProjectTask.setFieldValue("project", value);
                  formikNewProjectTask.setFieldValue("projectTask", null);
                  initialProjectTaskList(value);
                  setState((prevState) => ({
                    ...prevState,
                    projectsTaskListOptions: [],
                  }));
                }}
                onBlur={() => {
                  formikNewProjectTask.handleBlur("project");
                }}
                onSearch={(value) => onSearchProject(value)}
                filterOption={(input, option) => {
                  return (
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                    option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  );
                }}
                value={formikNewProjectTask.values.project}
              >
                {state.projectsListOptions.length > 0 &&
                  state.projectsListOptions.map((project, idx) => {
                    return (
                      <Option key={idx} value={project.id} name={project.name}>
                        {project.id} | {project.name}
                      </Option>
                    );
                  })}
              </Select>
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="projectTask">
                {t("odaAllocation.table.projectTask")}*
              </label>
            </Row>
            <Row>
              <Select
                disabled={isEmpty(formikNewProjectTask.values.project)}
                className="w-100"
                id="projectTask"
                optionFilterProp="value"
                name="projectTask"
                showSearch
                placeholder={t("odaAllocation.table.projectTask")}
                onChange={formikNewProjectTask.handleChange("projectTask")}
                onBlur={formikNewProjectTask.handleBlur("projectTask")}
                onSearch={(value) => onSearchProjectTask(value)}
                filterOption={(input, option) => {
                  return (
                    option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                    option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  );
                }}
                value={formikNewProjectTask.values.projectTask}
              >
                {state.projectsTaskListOptions.length > 0 &&
                  state.projectsTaskListOptions.map((projectTask, idx) => {
                    return (
                      <Option key={idx} value={projectTask.id} name={projectTask.name}>
                        {projectTask.id} | {projectTask.name}
                      </Option>
                    );
                  })}
              </Select>
            </Row>
          </Col>
        </Row>
      </Modal>
      <Modal
        open={state.editRateModal.visible}
        width={700}
        title={t("odaAllocation.editRate")}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikEditRate.submitForm}
            isDisabled={!(formikEditRate.dirty && formikEditRate.isValid)}
          />,
        ]}
        onCancel={closeRateModal}
      >
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="rate">
                {t("odaAllocation.table.hourlyRate")}*
              </label>
            </Row>
            <Row>
              {formatNumber({
                name: `rate`,
                value: formikEditRate.values.rate,
                lang: currentLanguage,
                id: `rate`,
                displayType: "input",
                suffix: "EUR",
                customInput: Input,
                onChange: formikEditRate.handleChange,
                onBlur: formikEditRate.handleBlur,
              })}
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default OdaAllocationDetail;
